'use client';

import React, { useEffect, useState, Fragment, useContext, useRef } from 'react';
import { supabase } from '../../supabaseClient';
import { Dialog, Transition } from '@headlessui/react';
import { EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';
import InviteModal from './InviteModal';
import { AuthContext } from '../../context/AuthContext';
import { Menu } from '@headlessui/react';
import { EllipsisVerticalIcon } from '@heroicons/react/20/solid';
import InviteCard from './InviteCard';
import CompanyKeyCard from './CompanyKeyCard';
import RemoveMemberModal from '../../utils/RemoveMemberModal'; // Import the new modal

function classNames(...classes) {
  return classes.filter(Boolean).join(' ');
}

export default function TeamSettings({ organizationId }) {
  const { user } = useContext(AuthContext);
  const [teamMembers, setTeamMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedMember, setSelectedMember] = useState(null); // Store the selected member
  const [newRole, setNewRole] = useState('');
  const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);
  const [userRole, setUserRole] = useState(''); // Store the logged-in user's role
  const [stripeCustomerId, setStripeCustomerId] = useState(null);
  const [isRemoveModalOpen, setIsRemoveModalOpen] = useState(false); // Control the remove modal
  const actionMenuRef = useRef(null); // Ref for click-away handler
  const [openActionMenu, setOpenActionMenu] = useState(null); // State for action menu

  useEffect(() => {
    const fetchCustomerId = async () => {
      const customerId = await fetchStripeCustomerId(organizationId);
      setStripeCustomerId(customerId);
    };

    fetchCustomerId();
  }, [organizationId]);

  const fetchStripeCustomerId = async (organizationId) => {
    try {
      if (!organizationId) {
        console.error('Organization ID is required to fetch the Stripe Customer ID');
        return null;
      }

      const { data, error } = await supabase
        .from('organizations')
        .select('stripe_customer_id')
        .eq('id', organizationId)
        .single();

      if (error) {
        console.error('Error fetching Stripe Customer ID:', error);
        return null;
      }

      return data?.stripe_customer_id || null;
    } catch (err) {
      console.error('Unexpected error fetching Stripe Customer ID:', err);
      return null;
    }
  };

  const handleInviteOpen = () => {
    setIsInviteModalOpen(true);
  };

  const fetchTeamData = async () => {
    if (!organizationId) return;

    setLoading(true);
    try {
      // Fetch the logged-in user's role in the organization
      const { data: currentUserRole, error: roleError } = await supabase
        .from('organization_members')
        .select('role')
        .eq('organization_id', organizationId)
        .eq('user_id', user.id)
        .single();

      if (roleError || !currentUserRole) {
        console.error('Error fetching user role:', roleError);
        return;
      }

      setUserRole(currentUserRole.role);

      // Fetch team members
      const { data: teamData, error: teamError } = await supabase
        .from('organization_members')
        .select(`
          id,
          user_id,
          role,
          profile (
            first_name,
            last_name,
            email_address
          ),
          organizations (
            name
          )
        `)
        .eq('organization_id', organizationId);

      // Fetch pending invites
      const { data: inviteData, error: inviteError } = await supabase
        .from('organization_invites')
        .select('id, email, role, status')
        .eq('organization_id', organizationId)
        .eq('status', 'pending');

      if (teamError || inviteError) {
        console.error('Error fetching data:', teamError || inviteError);
        return;
      }

      // Map team members
      const teamMembers = (teamData || []).map((member) => ({
        id: member.id,
        userid: member.user_id,
        role: member.role,
        firstName: member.profile?.first_name || 'N/A',
        lastName: member.profile?.last_name || 'N/A',
        email: member.profile?.email_address || 'N/A',
        isInvited: false,
      }));

      // Map pending invites
      const pendingInvites = (inviteData || []).map((invite) => ({
        id: invite.id,
        role: invite.role,
        firstName: 'N/A',
        lastName: 'N/A',
        email: invite.email,
        isInvited: true,
      }));

      // Combine both lists
      setTeamMembers([...teamMembers, ...pendingInvites]);
    } catch (err) {
      console.error('Unexpected error fetching data:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTeamData();
  }, [organizationId]);

  const openManageAccessModal = (member) => {
    setSelectedMember(member);
    setNewRole(member.role);
    setIsModalOpen(true);
  };

  const handleRoleChange = async () => {
    try {
      const { error } = await supabase
        .from('organization_members')
        .update({ role: newRole })
        .eq('id', selectedMember.id);

      if (error) {
        console.error('Error updating role:', error);
        alert('Failed to update role.');
      } else {
        setTeamMembers((prev) =>
          prev.map((member) =>
            member.id === selectedMember.id ? { ...member, role: newRole } : member
          )
        );
        alert('Role updated successfully!');
      }
    } catch (err) {
      console.error('Error updating role:', err);
      alert('Failed to update role.');
    } finally {
      setIsModalOpen(false);
    }
  };

  const cancelInvite = async (inviteId) => {
    try {
      const { error } = await supabase
        .from('organization_invites')
        .delete()
        .eq('id', inviteId);

      if (error) {
        console.error('Error canceling invite:', error);
        alert('Failed to cancel the invitation.');
      } else {
        alert('Invitation canceled successfully.');
        fetchTeamData(); // Refresh the data
      }
    } catch (err) {
      console.error('Unexpected error:', err);
      alert('Failed to cancel the invitation.');
    }
  };

  // New: Open the remove modal
  const handleRemoveMember = (member) => {
    if (!member.isInvited) { // Only for actual team members, not invites
      setSelectedMember(member);
      setIsRemoveModalOpen(true);
    }
  };

  // Update the handleMenuClick function at the top of the component:
const handleMenuClick = (memberId) => {
  setOpenActionMenu((prev) => (prev === memberId ? null : memberId));
  console.log('Toggling menu for memberId:', memberId, 'New state:', openActionMenu === memberId ? null : memberId); // Debug log
};

// Update the useEffect for click-away handler:
useEffect(() => {
  const handleClickOutside = (event) => {
    if (actionMenuRef.current && !actionMenuRef.current.contains(event.target)) {
      setOpenActionMenu(null);
    } else {
      console.log('Click inside or on menu, not closing'); // Debug log
    }
  };

  if (openActionMenu !== null) {
    document.addEventListener('mousedown', handleClickOutside);
  } else {
    document.removeEventListener('mousedown', handleClickOutside);
  }

  return () => {
    document.removeEventListener('mousedown', handleClickOutside);
  };
}, [openActionMenu]);

  return (
    <div>
      <InviteCard organizationId={organizationId} onInviteSuccess={fetchTeamData} stripeCustomerId={stripeCustomerId} />

      {/* Company Key Card */}
      <CompanyKeyCard organizationId={organizationId} />

      {/* Team members table */}
      <div className="overflow-hidden rounded-lg bg-white border border-gray-300 shadow-sm">
        <table className="min-w-full divide-y divide-gray-300 bg-white shadow-md rounded-md overflow-hidden">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-sm font-semibold text-gray-900">User</th>
              <th className="px-6 py-3 text-left text-sm font-semibold text-gray-900">Email</th>
              <th className="px-6 py-3 text-left text-sm font-semibold text-gray-900">Role</th>
              <th className="px-6 py-3 text-sm text-left font-semibold text-gray-900">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {loading ? (
              <tr>
                <td colSpan="4" className="px-6 py-4 text-center text-gray-500">
                  Loading team members...
                </td>
              </tr>
            ) : (
              teamMembers.map((member) => (
                <tr key={member.id} className="hover:bg-gray-50">
                  <td className="flex items-center px-6 py-4 text-sm text-gray-900">
                    <div className="ml-4">
                      {member.isInvited ? (
                        <p className="font-medium">
                          New Team Member
                          <span className="ml-2 inline-flex items-center rounded-full bg-orange-100 px-2.5 py-0.5 text-xs font-medium text-orange-800">
                            Invited
                          </span>
                        </p>
                      ) : (
                        <p className="font-medium">
                          {member.firstName} {member.lastName}
                        </p>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-600">{member.email}</td>
                  <td className="px-6 py-4 text-sm">{member.role.charAt(0).toUpperCase() + member.role.slice(1)}</td>
                  <td className="px-6 py-4 text-left text-sm">
  {member.isInvited ? (
    // Existing invite menu code remains unchanged
    <Menu as="div" className="relative inline-block text-left">
      <div>
        <Menu.Button className="inline-flex justify-center rounded-md bg-gray-100 px-3 py-1 text-sm font-medium text-gray-600 hover:bg-gray-200">
          Manage Invite
        </Menu.Button>
      </div>
      <Transition
        as={Fragment}
        enter="transition ease-out duration-100"
        enterFrom="transform opacity-0 scale-95"
        enterTo="transform opacity-100 scale-100"
        leave="transition ease-in duration-75"
        leaveFrom="transform opacity-100 scale-100"
        leaveTo="transform opacity-0 scale-95"
      >
        <Menu.Items className="absolute right-0 z-10 mt-2 w-56 origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
          <div className="py-1">
            <Menu.Item>
              {({ active }) => (
                <button
                  onClick={() => cancelInvite(member.id)}
                  className={classNames(
                    active ? 'bg-gray-100' : '',
                    'block w-full text-left px-4 py-2 text-sm text-gray-700'
                  )}
                >
                  Cancel Invitation
                </button>
              )}
            </Menu.Item>
          </div>
        </Menu.Items>
      </Transition>
    </Menu>
  ) : (
    <div className="relative">
      <button
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          handleMenuClick(member.id);
        }}
        className={`p-1 ml-2 rounded-full hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-yellow-500 ${
          (userRole !== 'admin' && userRole !== 'owner') || member.userid === user.id
            ? 'opacity-50 cursor-not-allowed'
            : ''
        }`}
        disabled={(userRole !== 'admin' && userRole !== 'owner') || member.userid === user.id}
        aria-label="Open actions menu"
      >
        <EllipsisVerticalIcon className="w-5 h-5 text-gray-600" />
      </button>
      {openActionMenu === member.id && (
        <div
          ref={actionMenuRef}
          className="absolute top-1/2 left-0 transform -translate-y-1/2 -translate-x-full w-36 bg-white border border-gray-200 rounded-md shadow-lg z-50"
          onClick={(e) => e.stopPropagation()}
        >
          <button
            onClick={() => {
              openManageAccessModal(member);
              setOpenActionMenu(null);
            }}
            className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
          >
            Manage Role
          </button>
          <button
            onClick={() => {
              handleRemoveMember(member);
              setOpenActionMenu(null);
            }}
            className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
          >
            Remove
          </button>
        </div>
      )}
    </div>
  )}
</td>
                </tr>
              ))
            )}
            <tr>
              <td className="px-6 py-6 text-sm text-gray-600"></td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* Modal for Manage Access */}
      <Transition.Root show={isModalOpen} as={Fragment}>
        <Dialog as="div" className="relative z-10" onClose={setIsModalOpen}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </Transition.Child>

          <div className="fixed inset-0 z-10 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              >
                <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white px-4 pt-5 pb-4 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
                  <div>
                    <div className="text-center sm:mt-5">
                      <Dialog.Title
                        as="h3"
                        className="text-lg font-medium leading-6 text-gray-900"
                      >
                        Manage Access
                      </Dialog.Title>
                      <div className="mt-2">
                        <p className="text-sm text-gray-500">
                          Update the role for {selectedMember?.firstName}{' '}
                          {selectedMember?.lastName}.
                        </p>
                        <select
                          value={newRole}
                          onChange={(e) => setNewRole(e.target.value)}
                          className="mt-4 w-full rounded-md border-gray-300 shadow-sm focus:border-yellow-500 focus:ring-yellow-500"
                        >
                          <option value="admin">Admin</option>
                          <option value="member">Member</option>
                        </select>
                      </div>
                    </div>
                  </div>
                  <div className="mt-5 sm:mt-6">
                    <button
                      type="button"
                      className="inline-flex w-full justify-center rounded-md border border-transparent bg-yellow-600 px-4 py-2 text-base font-medium text-white shadow-sm hover:bg-yellow-700 focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:ring-offset-2 sm:text-sm"
                      onClick={handleRoleChange}
                    >
                      Save Changes
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>

      {/* Remove Member Modal */}
      <RemoveMemberModal
        isOpen={isRemoveModalOpen}
        onClose={() => setIsRemoveModalOpen(false)}
        member={selectedMember}
        teamMembers={teamMembers}
        onRemoveSuccess={fetchTeamData}
        organizationId={organizationId}
      />
    </div>
  );
}